import React, { useMemo } from 'react';
import { Button } from 'primereact/button';

/* ─────────────────────────────────────────────────────────────
   HELPERS
───────────────────────────────────────────────────────────── */
const TODOS_DIAS = ['LUNES', 'MARTES', 'MIERCOLES', 'JUEVES', 'VIERNES', 'SABADO', 'DOMINGO'];

const DIAS_CORTOS = {
    LUNES: 'L', MARTES: 'Ma', MIERCOLES: 'Mi',
    JUEVES: 'J', VIERNES: 'V', SABADO: 'S', DOMINGO: 'D',
};

const formatHora = (fecha) =>
    fecha ? fecha.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' }) : '';

/* ─────────────────────────────────────────────────────────────
   SUB-COMPONENTES
───────────────────────────────────────────────────────────── */

/** Círculo de día: relleno si activo, gris si no */
const DiaChip = ({ dia, activo }) => (
    <span className={`dia-chip ${activo ? 'activo' : 'inactivo'}`}>
        {DIAS_CORTOS[dia]}
    </span>
);

/** Fila compacta de un turno */
const TurnoFila = ({ turno, index }) => (
    <div className="resumen-turno-fila">
        {/* Número */}
        <span>{index + 1}</span>

        {/* Nombre + chips de días */}
        <div className="resumen-turno-info">
            <p className="resumen-turno-nombre">{turno.idTurno.nombre}</p>
            <div className="resumen-turno-dias">
                {TODOS_DIAS.map((d) => (
                    <DiaChip key={d} dia={d} activo={turno.diasOperacion.includes(d)} />
                ))}
            </div>
        </div>

        {/* Tipo de servicio */}
        <span className="resumen-turno-servicio">
            {turno.tipoServicio.nombre}
        </span>

        {/* Horario */}
        <span className="resumen-turno-horario">
            <i className="pi pi-clock" />
            {turno.horaInicio ? formatHora(turno.horaInicio) : formatHora(turno.horaFin)}
        </span>
    </div>
);

/* ─────────────────────────────────────────────────────────────
   COMPONENTE PRINCIPAL
───────────────────────────────────────────────────────────── */
const PasoResumen = ({
                         nombreCotizacion,
                         clienteSeleccionado,
                         turnos = [],
                         onEditar,
                         tipoUnidad,
                         tiposVehiculo = [],
                         modoEdicion = false,
                     }) => {
    const nombreUnidad = useMemo(() => {
        const v = tiposVehiculo.find((v) => v.ID === tipoUnidad);
        return v ? v.TIPO : tipoUnidad;
    }, [tipoUnidad, tiposVehiculo]);

    const diasTotales = useMemo(() => {
        const set = new Set();
        turnos.forEach((t) => t.diasOperacion.forEach((d) => set.add(d)));
        return set.size;
    }, [turnos]);

    return (
        <div className="paso-resumen-v2">

            {/* ── Sección: Información Básica ── */}
            <div className="resumen-seccion">
                <div className="resumen-seccion-header">
                    <div className="resumen-seccion-title">
                        <i className="pi pi-info-circle" />
                        <span>Información Básica</span>
                    </div>
                    <Button
                        icon="pi pi-pencil"
                        label="Editar"
                        className="p-button-text p-button-sm resumen-btn-editar"
                        onClick={() => onEditar(0)}
                    />
                </div>

                <div className="resumen-info-grid">
                    <div className="resumen-info-item">
                        <label>Cotización</label>
                        <strong>{nombreCotizacion || '—'}</strong>
                    </div>

                    <div className="resumen-info-sep" />

                    <div className="resumen-info-item">
                        <label>Cliente</label>
                        <strong>{clienteSeleccionado?.nombre || '—'}</strong>
                    </div>

                    <div className="resumen-info-sep" />

                    <div className="resumen-info-item">
                        <label>Tipo de unidad</label>
                        <strong>
                            <i className="pi pi-car mr-2" style={{ color: 'var(--primary-color)' }} />
                            {nombreUnidad || '—'}
                        </strong>
                    </div>
                </div>
            </div>

            {/* ── Sección: Turnos ── */}
            <div className="resumen-seccion">
                <div className="resumen-seccion-header">
                    <div className="resumen-seccion-title">
                        <i className="pi pi-clock" />
                        <span>Turnos Configurados</span>
                        <span className="resumen-stats-inline">
                            <span className="resumen-stat-chip stat-primary">
                                <i className="pi pi-list" />
                                {turnos.length} turno{turnos.length !== 1 ? 's' : ''}
                            </span>
                            <span className="resumen-stat-chip stat-blue">
                                <i className="pi pi-calendar" />
                                {diasTotales} día{diasTotales !== 1 ? 's' : ''}
                            </span>
                        </span>
                    </div>
                    <Button
                        icon="pi pi-pencil"
                        label="Editar"
                        className="p-button-text p-button-sm resumen-btn-editar"
                        onClick={() => onEditar(1)}
                    />
                </div>

                {/* Cabecera de columnas */}
                <div className="resumen-tabla-header">
                    <span />
                    <span>Turno</span>
                    <span>Servicio</span>
                    <span>Horario</span>
                </div>

                {/* Filas */}
                <div className="resumen-tabla-body">
                    {turnos.length === 0 ? (
                        <p className="resumen-tabla-vacia">Sin turnos configurados.</p>
                    ) : (
                        turnos.map((turno, i) => (
                            <TurnoFila key={i} turno={turno} index={i} />
                        ))
                    )}
                </div>
            </div>

            {/* ── Aviso final ── */}
            <div className="resumen-aviso-final">
                <i className="pi pi-info-circle" />
                <span>
                    {modoEdicion
                        ? 'Al actualizar, los cambios serán guardados. Use "Editar" o "Anterior" para corregir.'
                        : 'Al crear, esta información quedará registrada. Use "Editar" o "Anterior" para corregir.'}
                </span>
            </div>

        </div>
    );
};

export default PasoResumen;
